import { prepackSportLabel } from '@/utils/kambiSports'
import { aiScore } from './prepackCardModel'
import { canonicalSport, packSportId } from './prepackRecipeModel'
import { isPrepackSurfaceLive } from './prepackSurfaceLive'

const WHITESPACE_PATTERN = /\s+/
const MATCHUP_SEPARATOR_PATTERN = /\s+(?:vs\.?|v\.?|@|at|-)\s+/i

const KNOWN_ACRONYM_LEAGUES = new Set(['NFL', 'NBA', 'MLB', 'NHL', 'WNBA', 'CFL', 'MLS', 'UFC', 'NCAAF', 'NCAAB', 'EPL'])

export function humanizeLeague(league) {
  if (!league || league === '—') return null
  const trimmed = String(league).trim()
  if (!trimmed) return null
  const upper = trimmed.toUpperCase()
  if (KNOWN_ACRONYM_LEAGUES.has(upper) || trimmed.length <= 4) return upper
  return trimmed
    .replaceAll('_', ' ')
    .replaceAll('-', ' ')
    .split(WHITESPACE_PATTERN)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export function humanizeSport(sport) {
  if (!sport || sport === '—') return null
  const trimmed = String(sport).trim()
  if (!trimmed) return null
  const upper = trimmed.toUpperCase()
  if (upper === 'MMA' || upper === 'UFC') return upper
  return trimmed
    .replaceAll('_', ' ')
    .replaceAll('-', ' ')
    .split(WHITESPACE_PATTERN)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export function eventGroup(event) {
  const league = humanizeLeague(event?.league)
  if (league) return league
  const sport = humanizeSport(event?.sport)
  if (sport) return sport
  return 'Other'
}

// Identity of the game a leg belongs to. Two games can share a label (a
// series, or the same matchup on another date), so the provider event id is
// the key and the label is only what the operator reads.
const eventKey = (leg) => String(leg?.event_id ?? leg?.eventId ?? leg?.event_label ?? '')

function packEventLegs(pack) {
  const legs = (pack.legs || []).filter((leg) => Boolean(leg?.event_label))
  if (legs.length > 0) return legs
  if (pack.event_label && pack.event_label !== '—') {
    return [{ event_id: pack.event_id, event_label: pack.event_label, league: pack.league, sport: pack.sport, event_start_time: pack.event_start_time }]
  }
  return []
}

/** Keys of every event a pack has a leg in. */
export function packEventKeys(pack) {
  return new Set(packEventLegs(pack).map(eventKey))
}

/**
 * One option per event across the given packs: `value` is the event key,
 * `eventId` the provider event id when the key is one (null when only the
 * label names the game), `count` the packs with a leg in it, earliest
 * kickoff first.
 */
export function eventOptions(packs = []) {
  const events = new Map()
  for (const pack of packs || []) {
    const seenInPack = new Set()
    for (const leg of packEventLegs(pack)) {
      const key = eventKey(leg)
      let entry = events.get(key)
      if (!entry) {
        const eventId = leg?.event_id ?? leg?.eventId
        entry = { value: key, eventId: eventId == null || eventId === '' ? null : String(eventId), label: leg.event_label, league: null, sport: null, startTime: null, count: 0, _teamNames: new Set() }
        events.set(key, entry)
      }
      entry.league ||= leg.league || pack.league || null
      entry.sport ||= leg.sport || pack.sport || null
      const incomingTime = leg.event_start_time || pack.event_start_time
      if (incomingTime) {
        const current = Date.parse(entry.startTime)
        const incoming = Date.parse(incomingTime)
        if (!entry.startTime || (Number.isFinite(incoming) && (!Number.isFinite(current) || incoming < current))) entry.startTime = incomingTime
      }
      for (const name of [leg.participant, leg.selection_name, leg.team, ...leg.event_label.split(MATCHUP_SEPARATOR_PATTERN)]) {
        if (name && String(name).trim()) entry._teamNames.add(String(name).trim())
      }
      if (!seenInPack.has(key)) {
        seenInPack.add(key)
        entry.count += 1
      }
    }
  }

  const list = [...events.values()].map(({ _teamNames, ...rest }) => ({ ...rest, teamNames: [..._teamNames] }))
  return list.sort((a, b) => {
    const timeA = Date.parse(a.startTime)
    const timeB = Date.parse(b.startTime)
    const hasA = Number.isFinite(timeA)
    const hasB = Number.isFinite(timeB)
    if (hasA && hasB && timeA !== timeB) return timeA - timeB
    if (hasA !== hasB) return hasA ? -1 : 1
    return a.label.localeCompare(b.label)
  })
}
export function groupValues(pack, dimension) {
  if (!dimension) return []
  const field = dimension === 'event' ? 'event_label' : dimension
  const values = (pack.legs || []).map((leg) => leg[field]).filter(Boolean)
  if (!values.length && pack[dimension] && pack[dimension] !== '—') values.push(pack[dimension])
  return [...new Set(values.map((value) => dimension === 'league' ? value.toUpperCase() : dimension === 'sport' ? value.charAt(0).toUpperCase() + value.slice(1) : value))]
}

// A sport's place in the filters' order (#1142): its index in `sportOrder`,
// then every other sport, then legs that name no sport at all.
const sportRank = (sportOrder) => {
  const rank = new Map(sportOrder.map((id, index) => [id, index]))
  return (sport) => rank.get(sport) ?? (sport === 'other' ? sportOrder.length + 1 : sportOrder.length)
}

// The builder sport a leg is in (packSportId: Kambi's FOOTBALL is soccer
// unless the league is an American one), from its pack's when the leg names
// none; 'other' when neither does.
const known = (value) => (value && value !== '—' ? value : '')
const legSportId = (leg, pack) => packSportId(known(leg?.sport) || known(pack?.sport), known(leg?.league) || known(pack?.league)) || 'other'

// The sport most of a value's legs are in; a tie goes to the earlier sport.
function mostNamed(counts, rankOf) {
  let best = null
  for (const [sport, count] of counts) {
    if (!best || count > best[1] || (count === best[1] && rankOf(sport) < rankOf(best[0]))) best = [sport, count]
  }
  return best?.[0] || 'other'
}

/**
 * The League pages filter's options grouped by sport (#1142): one group per
 * sport in `sportOrder` (the org's builder sports, its featured sports
 * first), sports outside it after, alphabetically, and "Other" last. Inside
 * a group the leagues keep the order the packs give them (the order the
 * filter always listed them in), each with the number of packs that have a
 * leg in it. A league's sport is the one most of its legs are in. `value`
 * is groupValues' (the league upper-cased), so a choice, activeSubcategory
 * and filterPageOf are unchanged.
 */
export function leagueGroups(packs = [], sportOrder = []) {
  const rankOf = sportRank(sportOrder)
  const leagues = new Map()
  for (const pack of packs || []) {
    for (const value of groupValues(pack, 'league')) {
      if (!value || value === '—') continue
      let entry = leagues.get(value)
      if (!entry) {
        entry = { value, label: humanizeLeague(value) || value, count: 0, sports: new Map() }
        leagues.set(value, entry)
      }
      entry.count += 1
      const legs = (pack.legs || []).filter((leg) => String(leg?.league || '').toUpperCase() === value)
      for (const leg of legs.length ? legs : [{}]) {
        const sport = legSportId(leg, pack)
        entry.sports.set(sport, (entry.sports.get(sport) || 0) + 1)
      }
    }
  }
  const groups = new Map()
  for (const { value, label, count, sports } of leagues.values()) {
    const sport = mostNamed(sports, rankOf)
    if (!groups.has(sport)) groups.set(sport, { sport, label: prepackSportLabel(sport), leagues: [] })
    groups.get(sport).leagues.push({ value, label, count })
  }
  return [...groups.values()].sort((a, b) => rankOf(a.sport) - rankOf(b.sport) || a.label.localeCompare(b.label))
}

/**
 * The Sport pages filter's values in the same sport order (#1142); values
 * outside it keep their order after. A value's sport is the one most of its
 * packs' legs are in (Kambi's "Football" is soccer unless its leagues are
 * American); a value no pack explains reads as canonicalSport does.
 */
export function orderSportValues(values = [], sportOrder = [], packs = []) {
  const rankOf = sportRank(sportOrder)
  const counts = new Map()
  for (const pack of packs || []) {
    for (const value of groupValues(pack, 'sport')) {
      const legs = (pack.legs || []).filter((leg) => leg?.sport && groupValues({ legs: [leg] }, 'sport')[0] === value)
      for (const leg of legs.length ? legs : [{}]) {
        const sport = legSportId(leg, pack)
        if (!counts.has(value)) counts.set(value, new Map())
        counts.get(value).set(sport, (counts.get(value).get(sport) || 0) + 1)
      }
    }
  }
  const sportOf = (value) => (counts.has(value) ? mostNamed(counts.get(value), rankOf) : canonicalSport(value))
  return [...values].sort((a, b) => rankOf(sportOf(a)) - rankOf(sportOf(b)))
}

/**
 * The page an embed names for a Surfaces filter choice, as the widget sends
 * it (`sport`, `league`, `event_id`), or null when there is none: a game by
 * its provider id (a game known only by its label has no page), a league
 * with its sport when its packs' legs agree on one (an embed names a league
 * page with both, and that is its own lineup), a sport alone. `value` is the
 * filter's (groupValues for a sport or league, the event key for a game).
 */
export function filterPageOf(dimension, value, packs = [], events = []) {
  if (!dimension || !value) return null
  if (dimension === 'event') {
    const eventId = events.find((event) => event.value === value)?.eventId
    return eventId ? { event_id: eventId } : null
  }
  if (dimension === 'league') {
    const sports = new Set((packs || []).flatMap((pack) => (pack.legs || [])
      .filter((leg) => String(leg.league || '').toUpperCase() === value)
      .map((leg) => String(leg.sport || '').trim().toLowerCase())
      .filter(Boolean)))
    return sports.size === 1 ? { sport: [...sports][0], league: value } : { league: value }
  }
  return { sport: value.toLowerCase() }
}

export function rankSurface(packs, surface) {
  const locks = surface.layout_config?.locked_positions || {}
  const mode = surface.layout_config?.ranking_mode || 'ai'
  const sorted = [...packs].sort((a, b) => (mode === 'recency' ? 0 : (aiScore(b) ?? -1) - (aiScore(a) ?? -1)) || Date.parse(b.created_at || 0) - Date.parse(a.created_at || 0) || String(a.id).localeCompare(String(b.id)))
  const result = Array(packs.length)
  const unlocked = []
  for (const pack of sorted) {
    const index = Number(locks[pack.id]) - 1
    if (Number.isInteger(index) && index >= 0 && index < result.length && !result[index]) result[index] = pack
    else unlocked.push(pack)
  }
  let cursor = 0
  for (let index = 0; index < result.length; index += 1) if (!result[index]) result[index] = unlocked[cursor++]
  return result
}

/**
 * The surfaces a pack is on, as the remove dialog lists them (#999): the
 * surface the operator is on first, then the rest in sidebar order, then
 * slugs the organization has no surface for any more (deleted in Design),
 * which are still assignments and so still removable. The current surface is
 * listed even when a stale pack lacks it: Remove started there.
 */
export function packSurfaceOptions(pack, surfaces = [], currentSlug = null) {
  const assigned = new Set([...(currentSlug ? [currentSlug] : []), ...(pack?.surfaces || [])])
  const known = new Set(surfaces.map((surface) => surface.slug))
  const options = [
    ...surfaces.filter((surface) => assigned.has(surface.slug)).map((surface) => ({ slug: surface.slug, name: surface.name || surface.slug, live: isPrepackSurfaceLive(surface), known: true })),
    ...[...assigned].filter((slug) => !known.has(slug)).map((slug) => ({ slug, name: slug, live: false, known: false })),
  ].map((option) => ({ ...option, current: option.slug === currentSlug }))
  return [...options.filter((option) => option.current), ...options.filter((option) => !option.current)]
}

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`
// How long the toast after Remove packs offers Undo. The server keeps a
// removal undoable for an hour (services.PlacementUndoWindow), and Recent
// removals offers it for that long.
export const REMOVAL_UNDO_TOAST_MS = 10_000
// The statuses a pack goes back to Draft from when it leaves its last surface.
const LIVE_STATUSES = new Set(['published', 'scheduled'])

/**
 * Remove packs (#1090): the surfaces the schedule's packs are on, from the
 * removal preview, in the shape packSurfaceOptions answers: the surface the
 * operator is on first, then the rest (the server orders known surfaces by
 * name, the ones Design deleted last). The current surface is listed even
 * when the preview lacks it: Remove started there.
 */
export function removalSurfaceOptions(preview, currentSlug) {
  const options = (preview?.surfaces || []).map((surface) => ({
    slug: surface.slug, name: surface.name || surface.slug, live: Boolean(surface.live), known: surface.known !== false, current: surface.slug === currentSlug,
  }))
  if (currentSlug && !options.some((option) => option.current)) options.unshift({ slug: currentSlug, name: currentSlug, live: false, known: false, current: true })
  return [...options.filter((option) => option.current), ...options.filter((option) => !option.current)]
}

/**
 * The packs and surfaces one choice of the Remove packs dialog affects.
 * packScope is 'pack' (`pack` alone) or 'schedule' (the preview's packs);
 * surfaceScope is 'current', 'all' (every surface those packs are on) or
 * 'choose' (`chosen`, a Set of slugs, with the current surface always
 * included). Packs are counted once however many of the surfaces they are
 * on (`count`); `leftNowhere` are the ones left on no surface, whatever
 * their status, `toDraft` the live ones among them (they go back to Draft),
 * and `othersThere` the schedule's other packs on the surfaces (what Stop
 * future packs hides too when only this pack is removed).
 */
export function removalImpact({ preview, pack, packScope, surfaceScope, chosen, currentSlug }) {
  const schedulePacks = preview?.packs || []
  const packs = packScope === 'schedule' ? schedulePacks : (pack ? [pack] : [])
  let surfaces
  if (surfaceScope === 'all') {
    surfaces = [...new Set([currentSlug, ...packs.flatMap((p) => p.surfaces || [])].filter(Boolean))]
  } else if (surfaceScope === 'choose') {
    surfaces = [...new Set([currentSlug, ...(chosen || [])].filter(Boolean))]
  } else {
    surfaces = currentSlug ? [currentSlug] : []
  }
  const selected = new Set(surfaces)
  const affected = packs.filter((p) => (p.surfaces || []).some((slug) => selected.has(slug)))
  const packIds = [...new Set(affected.map((p) => p.id))]
  const nowhere = affected.filter((p) => (p.surfaces || []).every((slug) => selected.has(slug)))
  const leftNowhere = [...new Set(nowhere.map((p) => p.id))]
  const toDraft = [...new Set(nowhere.filter((p) => LIVE_STATUSES.has(p.status)).map((p) => p.id))]
  const othersThere = new Set(schedulePacks.filter((p) => p.id !== pack?.id && (p.surfaces || []).some((slug) => selected.has(slug))).map((p) => p.id)).size
  // The preview lists only the schedule's newest packs when it has more
  // (#1090): the count is then a lower bound.
  const truncated = packScope === 'schedule' && Boolean(preview?.truncated)
  return { packIds, surfaces, leftNowhere, toDraft, count: packIds.length, othersThere, truncated }
}

// Where a removed pack stays listed (#1080): under its schedule's Packs, or,
// with no live schedule, under Schedules → Unscheduled packs. Never the
// Library, which operators no longer see.
const listedUnder = (schedule, many = false) => (schedule && !schedule.deleted
  ? `${many ? 'their' : 'its'} schedule’s Packs`
  : 'Schedules → Unscheduled packs')

// "goes"/"go", "is"/"are" for a count.
const verb = (count, one, many) => (count === 1 ? one : many)

const surfacesLabel = (slugs, options = [], all = false) => {
  if (slugs.length === 1) return options.find((option) => option.slug === slugs[0])?.name || slugs[0]
  return all ? `all ${slugs.length} surfaces` : plural(slugs.length, 'surface')
}

/**
 * The Remove packs dialog's live summary of one choice (removalImpact):
 * how many packs leave which surfaces, which are left on no surface (and
 * which of those go back to Draft), where they stay listed (#1080) and,
 * with Stop future packs, what the schedule switch does. A count from a
 * truncated preview says it is a lower bound.
 */
export function removalSummary(impact, { options = [], schedule = null, packScope = 'pack', surfaceScope = 'current', stopFuture = false } = {}) {
  const { count, surfaces, toDraft = [], leftNowhere = toDraft, truncated = false } = impact
  if (count === 0) return 'No packs are on the selected surfaces.'
  const where = surfacesLabel(surfaces, options, surfaceScope === 'all')
  const packs = count === 1 ? '1 pack' : `${count} packs`
  const sentences = [truncated ? `At least ${packs} will be removed from ${where}.` : `${packs} will be removed from ${where}.`]
  const nowhere = leftNowhere.length
  const drafted = toDraft.length
  const under = listedUnder(schedule, count !== 1)
  if (nowhere === 0) {
    sentences.push(count === 1 ? `It stays on its other surfaces and listed under ${under}.` : `They stay on their other surfaces and listed under ${under}.`)
  } else if (nowhere === count && drafted === count) {
    sentences.push(count === 1
      ? `It isn’t on any other surface, so it goes back to Draft. It stays listed under ${under}.`
      : `None of them is on another surface, so they go back to Draft. They stay listed under ${under}.`)
  } else if (nowhere === count && drafted === 0) {
    sentences.push(count === 1
      ? `It isn’t on any other surface. It stays listed under ${under}.`
      : `None of them is on another surface. They stay listed under ${under}.`)
  } else if (nowhere === count) {
    sentences.push(`None of them is on another surface; ${drafted} ${verb(drafted, 'goes', 'go')} back to Draft. They stay listed under ${under}.`)
  } else {
    let sentence = `${nowhere} of them ${verb(nowhere, 'is', 'are')} on no other surface`
    if (drafted === nowhere) sentence += ` and ${verb(drafted, 'goes', 'go')} back to Draft`
    else if (drafted > 0) sentence += `; ${drafted} ${verb(drafted, 'goes', 'go')} back to Draft`
    sentences.push(`${sentence}. All stay listed under ${under}.`)
  }
  if (stopFuture && schedule) {
    sentences.push(`New packs from “${schedule.name || 'this schedule'}” won’t show on ${where} until it is switched back on in Connected schedules.`)
    if (packScope === 'pack' && impact.othersThere > 0) sentences.push('Its other packs there are hidden too.')
  }
  if (truncated) sentences.push('Not every pack of the schedule could be counted; the count is of the newest ones.')
  return sentences.join(' ')
}

/**
 * The toast after Remove packs: how many packs left which surfaces, and how
 * many went back to Draft. `priorStatus` maps pack ids to their status
 * before (the preview's packs): a pack that was a draft already did not
 * move to Draft. A pack it does not know is taken as live.
 */
export function placementsRemovedMessage(result, options = [], priorStatus = {}) {
  const removed = result?.removed || []
  const stopped = result?.stopped_on || []
  if (removed.length === 0) {
    return stopped.length > 0
      ? `Nothing left to remove. The schedule is switched off on ${surfacesLabel(stopped, options)}`
      : 'Nothing to remove: those packs are already off those surfaces'
  }
  const where = surfacesLabel(result.surfaces || [], options)
  const drafted = removed.filter((p) => p.status === 'draft' && LIVE_STATUSES.has(priorStatus[p.id] ?? 'published')).length
  let message = removed.length === 1
    ? `Pack removed from ${where}${drafted ? ' and moved to Draft' : ''}`
    : `${removed.length} packs removed from ${where}${drafted ? `; ${drafted} moved to Draft` : ''}`
  if (stopped.length > 0) message += `. Its schedule is switched off on ${surfacesLabel(stopped, options)}`
  return message
}

const namesLabel = (slugs, options = []) => {
  const names = slugs.map((slug) => options.find((option) => option.slug === slug)?.name || slug)
  if (names.length <= 1) return names[0] || ''
  return names.length === 2 ? `${names[0]} and ${names[1]}` : plural(names.length, 'surface')
}

/**
 * One Recent removals row (#1090): "3 packs from “NBA Threes” off Home page
 * and Sport pages; schedule switched off on Home page". `options` are
 * { slug, name } as removalSurfaceOptions/packSurfaceOptions answer; a slug
 * they lack is shown as is.
 */
export function removalRowSummary(removal, options = []) {
  const packs = plural(removal?.packs?.length || 0, 'pack')
  const schedule = removal?.schedule_name ? ` from “${removal.schedule_name}”` : ''
  let summary = `${packs}${schedule} off ${namesLabel(removal?.surfaces || [], options)}`
  const stopped = removal?.stopped_on || []
  if (stopped.length > 0) summary += `; schedule switched off on ${namesLabel(stopped, options)}`
  return summary
}

/** The toast after Undo: what came back, and what could not. */
export function placementsRestoredMessage(result) {
  const restored = result?.restored?.length || 0
  const skipped = result?.skipped?.length || 0
  const message = restored === 1 ? 'Restored 1 pack' : `Restored ${restored} packs`
  return skipped ? `${message}; ${skipped} could not be restored` : message
}

// Why an Undo left a pack removed (services.PlacementSkip*).
const SKIP_REASONS = { deleted: 'deleted', changed: 'changed since the removal', unavailable: 'no longer available' }

/**
 * What a partial Undo left removed: 'Still removed: “A” (deleted), “B”
 * (changed since the removal)'; null when nothing was skipped.
 */
export function restoredSkippedDetails(skipped = []) {
  if (!skipped?.length) return null
  const packs = skipped.map((p) => `“${p.title || 'Untitled pack'}” (${SKIP_REASONS[p.reason] || p.reason || 'not restored'})`)
  return `Still removed: ${packs.join(', ')}`
}
